import { Link } from "react-router-dom";
import { ChevronRight, Info, ShieldCheck, AlertTriangle, Check } from "lucide-react";
import { Seo } from "../../components/Seo";

export const DRAFT_NOTICE = "Hinweis: Dieser Text ist ein professionell vorbereiteter Entwurf und wurde noch nicht abschließend anwaltlich geprüft. Die finale Fassung wird vor dem offiziellen Livegang nach juristischer Prüfung eingesetzt.";

/* Warmes Seiten-Gerüst: dunkler Mini-Hero mit Glow + helle Inhaltsfläche */
export function InfoPage({ title, lead, seoDesc, draft = false, updated, children, wide = false, testid, eyebrow = "KLEDIO" }) {
  return (
    <div className="bg-[#FAF9F5]" data-testid={testid}>
      <Seo title={title} description={seoDesc || lead} />
      <div className="relative bg-[#111111] text-white overflow-hidden">
        <div aria-hidden="true" className="absolute -top-28 -right-28 w-[420px] h-[420px] rounded-full opacity-25"
          style={{ background: "radial-gradient(circle, #FF6B35 0%, transparent 65%)" }} />
        <div className={`relative ${wide ? "max-w-5xl" : "max-w-3xl"} mx-auto px-4 sm:px-6 py-12 md:py-16`}>
          <p className="fade-up inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.25em] uppercase text-accent">
            <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" /> {eyebrow}
          </p>
          <h1 className="fade-up-1 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-3 max-w-2xl leading-[1.08] break-words" style={{ hyphens: "auto" }} lang="de">{title}</h1>
          {lead && <p className="fade-up-2 text-base md:text-lg text-white/70 mt-4 max-w-xl leading-relaxed">{lead}</p>}
          {updated && <p className="fade-up-3 mt-3 text-xs text-white/50">Stand: {updated}</p>}
        </div>
      </div>
      <div className={`${wide ? "max-w-5xl" : "max-w-3xl"} mx-auto px-4 sm:px-6 py-10 md:py-14`}>
        {draft && (
          <p className="mb-8 text-xs bg-white border border-[#E5E3DA] rounded-xl p-3.5 text-muted-foreground" data-testid="draft-notice">{DRAFT_NOTICE}</p>
        )}
        <div className="space-y-10 md:space-y-12">{children}</div>
      </div>
    </div>
  );
}

export function Section({ title, children }) {
  return (
    <section>
      {title && (
        <h2 className="font-display text-lg md:text-xl font-bold mb-4 flex items-center gap-3">
          <span aria-hidden="true" className="w-1.5 h-5 rounded-full bg-accent inline-block shrink-0" /> {title}
        </h2>
      )}
      <div className="space-y-3 text-sm text-foreground/85 leading-relaxed">{children}</div>
    </section>
  );
}

export function P({ children }) {
  return <p className="text-sm text-foreground/85 leading-relaxed">{children}</p>;
}

export function UL({ items }) {
  return (
    <ul className="space-y-2.5">
      {items.map((it, i) => (
        <li key={i} className="flex gap-2.5 text-sm text-foreground/85 leading-relaxed">
          <span className="w-4.5 h-4.5 w-5 h-5 rounded-full bg-accent/10 text-accent flex items-center justify-center shrink-0 mt-0.5">
            <Check className="w-3 h-3" aria-hidden="true" />
          </span>
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

/* Verbundene Timeline mit orangen Nummern-Kreisen */
export function Steps({ items }) {
  return (
    <ol className="relative">
      {items.map((s, i) => (
        <li key={i} className="group relative flex gap-4 pb-6 last:pb-0">
          {i < items.length - 1 && <span aria-hidden="true" className="absolute left-[15px] top-9 bottom-0 w-px bg-[#E5E3DA] group-hover:bg-accent/40 transition-colors" />}
          <span className="relative z-10 w-8 h-8 rounded-full bg-accent/10 text-accent border border-accent/30 text-[13px] font-bold flex items-center justify-center shrink-0 transition-all group-hover:bg-accent group-hover:text-white group-hover:scale-110">
            {i + 1}
          </span>
          <div className="pt-1">
            <p className="text-sm font-bold leading-snug">{s.t}</p>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{s.d}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

const TONES = {
  info: { icon: Info, cls: "bg-white border-[#E5E3DA]" },
  trust: { icon: ShieldCheck, cls: "bg-accent/5 border-accent/30" },
  warn: { icon: AlertTriangle, cls: "bg-amber-50 border-amber-200" },
};

export function InfoBox({ tone = "info", title, children }) {
  const T = TONES[tone];
  return (
    <div className={`border rounded-2xl p-4.5 p-5 flex gap-3.5 ${T.cls}`}>
      <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${tone === "warn" ? "bg-amber-100 text-amber-600" : "bg-accent/10 text-accent"}`}>
        <T.icon className="w-4.5 h-4.5 w-5 h-5" aria-hidden="true" />
      </span>
      <div>
        {title && <p className="text-sm font-bold">{title}</p>}
        <div className="text-sm text-foreground/80 leading-relaxed mt-0.5">{children}</div>
      </div>
    </div>
  );
}

export function LinkGrid({ links }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3.5">
      {links.map(([label, to, desc]) => (
        <Link key={to + label} to={to} className="card-lift border border-[#E5E3DA] rounded-2xl p-4.5 p-5 bg-white hover:border-accent/40 group flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-bold group-hover:text-accent transition-colors">{label}</p>
            {desc && <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>}
          </div>
          <span className="w-8 h-8 rounded-full bg-accent/10 text-accent flex items-center justify-center shrink-0 transition-transform group-hover:translate-x-0.5">
            <ChevronRight className="w-4 h-4" aria-hidden="true" />
          </span>
        </Link>
      ))}
    </div>
  );
}

export function Faq({ items, testid = "faq-list" }) {
  return (
    <div className="space-y-3" data-testid={testid}>
      {items.map((f, i) => (
        <details key={i} className="card-lift border border-[#E5E3DA] rounded-2xl bg-white group open:border-accent/40">
          <summary className="flex items-center justify-between gap-3 cursor-pointer list-none p-4.5 p-5 text-sm font-bold">
            {f.q}
            <span className="w-7 h-7 rounded-full bg-accent/10 text-accent flex items-center justify-center shrink-0 transition-transform group-open:rotate-90">
              <ChevronRight className="w-4 h-4" aria-hidden="true" />
            </span>
          </summary>
          <div className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed">{f.a}</div>
        </details>
      ))}
    </div>
  );
}
